import { defineComponent as _defineComponent } from 'vue'
import { ApiRequest, employeeApi, openShiftResponseApi, shiftApi } from '@/api';
import InputGroup from '@/components/form/InputGroup.vue';
import IconMessageBlock from '@/components/info/IconMessageBlock.vue';
import Chip from '@/components/interface/Chip.vue';
import LoadingDot from '@/components/loaders/LoadingDot.vue';
import BottomSheet from '@/components/modal/BottomSheet.vue';
import i18n from '@/i18n';
import { ApiVersion } from '@/lib/api/types';
import { useOpen } from '@/lib/composition/dialog';
import { belongsToEmployee } from '@/lib/filters/employeeFilters';
import { IconSettings } from '@/lib/mobileHeader';
import { useUserCan } from '@/lib/permission/userCanComposable';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { SortDirections } from '@/lib/schedule-v2/ScheduleV2Filter';
import {
  employeeNameSort,
  isNightShift,
} from '@/lib/schedule-v2/scheduleV2Functions';
import { isOpenAndUnassigned } from '@/lib/schedule-v2/shiftV2Status';
import store from '@/store';
import { getEntity, getEntityName } from '@/util/entityFunctions';
import { keyOfFirstTruthyValue } from '@/util/objectFunctions';
import { viewport } from '@/util/windowFunctions';
import ShiftSummary from '@/views/schedule/components/ShiftSummary.vue';
import spacetime from 'spacetime';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import {
  CompanySettingStartOfWeekEnum,
  Employee,
  EmployeeApi,
  EmployeeWithAvailability,
  JobRole,
  ListAvailableEmployeesRequest,
  OpenShiftResponse,
  OpenShiftResponseStatusEnum,
  Shift,
  WorkingTimeCost,
  type ListAvailableEmployeesResponse,
} from '../../../../api/v1';
import Button from '../../../components/buttons/Button.vue';
import Dialog from '../../../components/dialog/Dialog.vue';
import EmployeeListItem from '../../../components/employee/EmployeeListItem.vue';
import InlineDialog from '../../../components/info/InlineDialog.vue';
import ToolTipWrapper from '../../../components/info/ToolTipWrapper.vue';
import Search from '../../../components/search/Search.vue';
import { dateTimeFormats } from '../../../lang/dateTimeFormats';
import {
  employeeNameFilter,
  fullName,
  isZeroHourEmployee,
} from '../../../lib/employee/employeeFunctions';
import { Icon } from '../../../lib/enum/Icon';
import { isInPeriod } from '../../../lib/filters/dateFilters';
import { Entity } from '../../../lib/realtime/RealtimeEntity';
import {
  totalDurationOfShiftsInHours,
  totalDurationOfShiftsInMinutes,
} from '../../../lib/schedule-v2/shiftV2Functions';
import { showError, showSuccess } from '../../../plugins/ToastNotifications';
import {
  arrayLengthSort,
  flattenArray,
  groupBy,
  sortBy,
} from '../../../util/arrayFunctions';
import { formatCurrency } from '../../../util/currencyFunctions';
import { addMinutes, isFuture } from '../../../util/dateArithmetic';
import { convertPatternToDays, timeAgo } from '../../../util/dateFunctions';
import ShiftDialogHeader from './components/ShiftDialogHeader.vue';

enum SortOrders {
  AvailableHours = 'availableHours',
  Name = 'name',
  Hours = 'hours',
  ShiftCount = 'shiftCount',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AssignShiftV2',
  props: {
    value: { type: Boolean },
    shift: null,
    matchingShiftIds: { default: () => [] },
    isReplacement: { type: Boolean, default: false },
    showSchedule: { type: Boolean, default: false },
    showShiftHistory: { type: Boolean, default: true }
  },
  emits: ["input", "close", "openShiftHistory"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void;
  (e: 'close'): void;
  (e: 'openShiftHistory'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: boolean, shift: Shift, matchingShiftIds: number[], isReplacement: boolean, showSchedule: boolean, showShiftHistory: boolean }





const open = useOpen(() => props.value, emit);

const userCanWithScope = useUserCan(store.getters.loggedInEmployee);

const availableEmployees = reactive<{
  loading: boolean;
  data: EmployeeWithAvailability[];
}>({ loading: true, data: [] });

const openShiftResponses = ref<OpenShiftResponse[]>([]);
const selectedEmployees = ref<number[]>([]);
const query = ref<string>('');

const employees = realtimeQuery(Entity.Employee).fetch();
const jobRoles = realtimeQuery(Entity.JobRole).fetch();

const employeeCosts = ref<WorkingTimeCost[]>([]);

const workPatterns = realtimeQuery(Entity.WorkPattern).fetch();
const shiftCollection = ref(realtimeQuery(Entity.Shift).empty());
// Due to some of the filtering involved with this, we can't use a realtimeQuery
const weeklyOpenShiftResponses = ref<OpenShiftResponse[]>([]);
const schedules = realtimeQuery(Entity.Schedule).fetch();
const groups = realtimeQuery(Entity.EmployeeGroup).fetch();

const loading = ref<boolean>(false);
const initialising = computed<boolean>(
  () =>
    workPatterns.isLoading || employees.isLoading || availableEmployees.loading,
);

const showSort = ref<boolean>(false);
const sortEmployees = reactive({
  sortBy: SortOrders.AvailableHours,
  direction: SortDirections.Descending,
});

const startOfWeek = computed<CompanySettingStartOfWeekEnum>(
  () => store.state.settings.companySettings.startOfWeek,
);
const timezone = computed<string>(() => store.getters.timezone);
const currencyCode = computed<string>(
  () => store.getters['settings/currencyCode'],
);
const openShiftsEnabled = computed<boolean>(
  () => store.state.settings.scheduleSettings.enableOpenShifts,
);

const shiftOpenAndUnassigned = computed<boolean>(() => {
  return isOpenAndUnassigned(props.shift);
});

const hasResponses = computed<boolean>(
  () => openShiftResponses.value.length > 0,
);

const extendedSortOrders = computed(() => {
  return {
    ...(shiftOpenAndUnassigned.value &&
      hasResponses.value && { ResponseTime: 'responseTime' }),
    ...SortOrders,
  };
});

const respondedEmployees = computed<Employee[]>(() => {
  return employees.data.filter((e) =>
    openShiftResponses.value.some((r) => r.employeeId === e.id),
  );
});

const getShiftsForEmployee = (employeeId: number): Shift[] => {
  return shiftCollection.value.data.filter(belongsToEmployee(employeeId));
};

const filteredEmployees = computed<Employee[]>(() => {
  // If we have responses to this shift, use those as our starting point, rather than all available employees
  const employees = respondedEmployees.value.length
    ? respondedEmployees.value
    : availableEmployees.data.map((e) => e.employee);
  return employees
    .filter(employeeNameFilter(query.value))
    .sort((a: Employee, b: Employee) => {
      const multiplier =
        sortEmployees.direction === SortDirections.Ascending ? 1 : -1;

      const { sortBy } = sortEmployees;

      if (sortBy === extendedSortOrders.value.Name) {
        return employeeNameSort(a, b) * multiplier;
      }

      const shiftsA = getShiftsForEmployee(a.id);
      const shiftsB = getShiftsForEmployee(b.id);

      if (sortBy === extendedSortOrders.value.ShiftCount) {
        return arrayLengthSort(shiftsA, shiftsB) * multiplier;
      }
      if (sortBy === extendedSortOrders.value.Hours) {
        return (
          (totalDurationOfShiftsInMinutes(shiftsA) -
            totalDurationOfShiftsInMinutes(shiftsB)) *
          multiplier
        );
      }
      if (sortBy === extendedSortOrders.value.AvailableHours) {
        const shiftMinutesA = totalDurationOfShiftsInMinutes(shiftsA);
        const shiftMinutesB = totalDurationOfShiftsInMinutes(shiftsB);

        const workMinutesA = a.workingHoursPerWeek * 60;
        const workMinutesB = b.workingHoursPerWeek * 60;

        return (
          (workMinutesA - shiftMinutesA - (workMinutesB - shiftMinutesB)) *
          multiplier
        );
      }

      if (
        sortBy === extendedSortOrders.value.ResponseTime &&
        hasResponses.value
      ) {
        const getResponse = (employeeId: number) =>
          openShiftResponses.value.find(
            (response) => response.employeeId === employeeId,
          );
        const responseA = getResponse(a.id);
        const responseB = getResponse(b.id);
        return (responseB.createdAt - responseA.createdAt) * multiplier;
      }

      return 0;
    });
});

const assignedEmployee = computed<Employee>(() => {
  if (!props.shift.employeeId) {
    return null;
  }
  return employees.data.find((e) => e.id === props.shift.employeeId);
});

const adjustedShift = computed<Shift>(() => {
  if (isFuture(props.shift.startsAt)) {
    return props.shift;
  }
  const newStartsAt = spacetime(
    isFuture(props.shift.startsAt) ? props.shift.startsAt : new Date(),
    timezone.value,
  )
    .next('quarterHour')
    .toNativeDate();
  return {
    ...props.shift,
    startsAt: newStartsAt,
    scheduledBreaks: props.shift.scheduledBreaks.filter(
      (b) => !b.startsAt || b.startsAt > newStartsAt,
    ),
    shiftAreaSessions: props.shift.shiftAreaSessions
      .filter((s) => s.endTime > newStartsAt)
      .map((s) => ({
        ...s,
        startTime: s.startTime > newStartsAt ? s.startTime : newStartsAt,
      })),
  };
});

const submitString = computed<string>(() => {
  if (!props.matchingShiftIds.length) {
    return i18n.tc('button.saveAssignSingle');
  }
  const selectedEmployeesTotal = selectedEmployees.value.length;
  return i18n.tc('button.saveAssign', selectedEmployeesTotal, {
    number: selectedEmployeesTotal,
  });
});

const openShiftHistory = (): void => {
  emit('openShiftHistory');
  open.value = false;
};

const headerRight = computed<IconSettings | null>(() => {
  if (!props.showShiftHistory) {
    return null;
  }
  return {
    icon: Icon.ClockRotateLeft,
    action: openShiftHistory,
  };
});

const shortTitle = computed<string>(() => {
  if (assignedEmployee.value) {
    return i18n.tc('heading.reassignShift');
  }
  if (shiftOpenAndUnassigned.value) {
    if (hasResponses.value) {
      return i18n.tc('heading.approveOpenShiftFor');
    }
    return i18n.tc('heading.assignOpenShiftFor');
  }
  return i18n.tc('heading.assignFor');
});

const longTitle = computed<string>(() => {
  if (assignedEmployee.value) {
    return i18n.tc('schedule.dialog.findReplacementFor', 1, {
      e: fullName(assignedEmployee.value),
    });
  }
  return shortTitle.value;
});

const employeeIsAvailable = (employee: Employee): boolean => {
  return availableEmployees.data.some((e) => e.employee.id === employee.id);
};

const filteredAvailableEmployeesGrouped = computed<{
  [key: string]: Employee[];
}>(() => {
  const availableEmployees = filteredEmployees.value.filter((employee) =>
    employeeIsAvailable(employee),
  );

  return groupBy('employeeGroupId', availableEmployees);
});

const filteredUnavailableEmployees = computed<Employee[] | undefined>(() => {
  return filteredEmployees.value.filter(
    (employee) => !employeeIsAvailable(employee),
  );
});

const subHeading = computed<string>(() => {
  if (shiftOpenAndUnassigned.value) {
    return `${i18n.tc('heading.shiftRequests')} (${
      flattenArray(Object.values(filteredAvailableEmployeesGrouped.value))
        .length
    })`;
  }
  return i18n.tc('subHeading.employees');
});

const getAvailableEmployees = async (): Promise<void> => {
  if (shiftOpenAndUnassigned.value) {
    const { data: responses } =
      await openShiftResponseApi.listOpenShiftResponses({
        where: {
          shiftId: { eq: props.shift.id },
          status: {
            in: [
              OpenShiftResponseStatusEnum.Pending,
              OpenShiftResponseStatusEnum.Unavailable,
            ],
          },
        },
      });
    openShiftResponses.value = responses;
  }

  const additionalParams =
    shiftOpenAndUnassigned.value && hasResponses.value
      ? {
          where: {
            id: {
              in: openShiftResponses.value.map((r) => r.employeeId),
            },
          },
        }
      : {};

  await new ApiRequest<
    ApiVersion.V1,
    EmployeeApi,
    'listAvailableEmployees',
    ListAvailableEmployeesRequest,
    ListAvailableEmployeesResponse
  >(employeeApi, 'listAvailableEmployees', {
    startsAt: adjustedShift.value.startsAt,
    endsAt: adjustedShift.value.endsAt,
    jobRoleId: adjustedShift.value.jobRoleId,
    locationId: adjustedShift.value.locationId,
    scheduleId: adjustedShift.value.scheduleId,
    includeUnpublishedShifts: !(
      shiftOpenAndUnassigned.value && hasResponses.value
    ),
    _with: ['photo'],
    ...additionalParams,
  })
    .fetchAll()
    .then(({ data: employeesWithAvailability }) => {
      availableEmployees.data = employeesWithAvailability;
      availableEmployees.loading = false;
    });
};

const fetchShiftData = async () => {
  // We want only relevant shifts within the current week date range
  // and for the listed available employees
  if (!availableEmployees.data.length) {
    shiftCollection.value = realtimeQuery(Entity.Shift).empty(false);
    weeklyOpenShiftResponses.value = [];
    return;
  }
  const availableEmployeeIds = availableEmployees.data.map(
    (e) => e.employee.id,
  );
  const startOfPeriod = spacetime(props.shift.startsAt, timezone.value)
    .weekStart(startOfWeek.value)
    .startOf('week')
    .toNativeDate();
  const endOfPeriod = spacetime(props.shift.startsAt, timezone.value)
    .weekStart(startOfWeek.value)
    .endOf('week')
    .add(1, 'day')
    .startOf('day')
    .toNativeDate();

  shiftCollection.value = realtimeQuery(Entity.Shift)
    .whereIn('employeeId', availableEmployeeIds)
    .where('startsAt', 'lt', endOfPeriod)
    .where('endsAt', 'gt', startOfPeriod)
    .fetch();

  if (openShiftsEnabled.value) {
    const { data: openShiftResponsesForWeek } =
      await openShiftResponseApi.listOpenShiftResponses({
        where: {
          status: { eq: OpenShiftResponseStatusEnum.Accepted },
          'shift.startsAt': { lt: endOfPeriod },
          'shift.endsAt': { gt: startOfPeriod },
        },
      });
    weeklyOpenShiftResponses.value = openShiftResponsesForWeek;
  }
};

const employeeWorksDate = (employee: Employee): boolean => {
  const wp = getEntity(employee.workPatternId, workPatterns.data);
  const patternDays: string[] = convertPatternToDays(wp.pattern).map((d) =>
    d.toLowerCase(),
  );

  const worksDay = (date: Date) =>
    patternDays.includes(spacetime(date, timezone.value).dayName());
  return (
    worksDay(adjustedShift.value.startsAt) &&
    worksDay(adjustedShift.value.endsAt)
  );
};

const getShiftHoursForWeek = (employeeId: number): number => {
  return totalDurationOfShiftsInHours(getShiftsForEmployee(employeeId));
};

const hasUnpublishedShiftConflict = (employeeId: number): boolean => {
  return shiftCollection.value.data
    .filter(
      isInPeriod(
        adjustedShift.value.startsAt,
        adjustedShift.value.endsAt,
        'startsAt',
        'endsAt',
      ),
    )
    .some(belongsToEmployee(employeeId));
};

const employeeOverWeeklyHours = (employee: Employee): number => {
  const workedMaxDiff =
    getShiftHoursForWeek(employee.id) - employee.workingHoursPerWeek;
  return workedMaxDiff > 0 ? workedMaxDiff : 0;
};

const employeeCost = (employee: Employee): number => {
  return (
    employeeCosts.value.find((c) => c.employeeId === employee.id)?.cost || 0
  );
};

const selectEmployee = (employeeIds: number[]): void => {
  selectedEmployees.value = [...employeeIds];
};

const autoSelect = (): void => {
  const employees = flattenArray(
    Object.values(filteredAvailableEmployeesGrouped.value),
  );
  if (selectedEmployees.value.length || !employees.length) {
    selectedEmployees.value = [];
    return;
  }

  const employeePriority = employees.reduce((scoreboard, e) => {
    scoreboard[e.id] =
      Number(employeeWorksDate(e) && !employeeOverWeeklyHours(e)) +
      Number(employeeWorksDate(e)) +
      Number(!employeeOverWeeklyHours(e));
    return scoreboard;
  }, {});

  selectedEmployees.value = employees
    .slice()
    .sort((a, b) => employeePriority[b.id] - employeePriority[a.id])
    .map((e) => e.id)
    .slice(0, props.matchingShiftIds.length + 1);
};

// The toggle method for sorting employees
const changeEmployeeSortOrder = (order: SortOrders): void => {
  const { sortBy, direction } = sortEmployees;
  sortEmployees.sortBy = order;
  if (order === sortBy) {
    sortEmployees.direction =
      direction === SortDirections.Ascending
        ? SortDirections.Descending
        : SortDirections.Ascending;
  } else {
    sortEmployees.direction = SortDirections.Ascending;
  }
};

const assignShift = async (): Promise<void> => {
  loading.value = true;
  const shiftIds = [adjustedShift.value.id, ...props.matchingShiftIds];

  try {
    if (selectedEmployees.value.length === 1) {
      await shiftApi.updateShift({
        id: shiftIds[0],
        updateShiftData: {
          employeeId: selectedEmployees.value[0],
          // This data needs updating when reassigning shifts with the start time in the past - This only happens individually
          // (Employee hasn't shown up on time so the shift is being given to another employee but adjusting times for the future)
          startsAt: adjustedShift.value.startsAt,
          endsAt: adjustedShift.value.endsAt,
          scheduledBreaks: adjustedShift.value.scheduledBreaks,
          shiftAreaSessions: adjustedShift.value.shiftAreaSessions,
        },
      });
    } else {
      await shiftApi.bulkUpdateShifts({
        bulkUpdateShiftsData: {
          baseData: {},
          shiftData: selectedEmployees.value.map((employeeId, i) => ({
            shiftIds: [shiftIds[i]],
            data: {
              employeeId,
            },
          })),
        },
      });
    }

    showSuccess(
      i18n.tc('successMessage.assignedShift', selectedEmployees.value.length),
    );
    open.value = false;
  } catch {
    showError(
      i18n.tc('errorMessage.assignShift', selectedEmployees.value.length),
    );
  } finally {
    loading.value = false;
  }
};

const createReplacement = async (): Promise<void> => {
  await shiftApi
    .createShift({
      createShiftData: {
        ...adjustedShift.value,
        employeeId: selectedEmployees.value[0],
        published: !!adjustedShift.value.publishedAt,
      },
    })
    .then(() => {
      showSuccess(i18n.t('successMessage.createdReplacementShift'));
      open.value = false;
    })
    .catch(() => {
      showError(i18n.t('errorMessage.createReplacementShift'));
    });
};

const employeeListItemDisabled = (employee: Employee): boolean => {
  return (
    !employeeIsAvailable(employee) ||
    (selectedEmployees.value.length === props.matchingShiftIds.length + 1 &&
      !selectedEmployees.value.includes(employee.id))
  );
};

const getSameWeekShiftsForEmployee = (employeeId: number): Shift[] => {
  const filteredEmployeeShiftsForWeek = getShiftsForEmployee(employeeId);
  if (!selectedEmployees.value.includes(employeeId)) {
    return filteredEmployeeShiftsForWeek;
  }
  return [...filteredEmployeeShiftsForWeek, props.shift];
};

const shiftTimeInHours = (shifts: Shift[]): number => {
  const totalShiftTimesInMinutes = shifts.reduce(
    (total, shift) => total + shift.durationInMinutes,
    0,
  );
  return totalShiftTimesInMinutes / 60;
};

const dynamicShiftHoursForWeek = (employeeId: number): number => {
  return shiftTimeInHours(getSameWeekShiftsForEmployee(employeeId));
};

const getSameDayShiftsForEmployee = (employeeId: number): Shift[] => {
  const start = spacetime(props.shift.startsAt, timezone.value)
    .startOf('day')
    .toNativeDate();
  const buildEndDate = (numberOfDays) => {
    return spacetime(start, timezone.value)
      .add(numberOfDays, 'day')
      .startOf('day')
      .toNativeDate();
  };
  let end = buildEndDate(1);
  // if it's nightshift then include shifts on the day it finishes
  if (isNightShift(props.shift, timezone.value)) {
    end = buildEndDate(2);
  }
  const filteredEmployeeShiftsForWeek = getShiftsForEmployee(employeeId);
  let sameDayFilteredShifts = filteredEmployeeShiftsForWeek.filter(
    (shift) => shift.startsAt >= start && shift.startsAt < end,
  );
  if (selectedEmployees.value.includes(employeeId)) {
    sameDayFilteredShifts = [...sameDayFilteredShifts, props.shift];
  }
  sameDayFilteredShifts.sort(
    (a, b) => a.startsAt.getTime() - b.startsAt.getTime(),
  );
  return sameDayFilteredShifts;
};

const getShiftHoursForDay = (employeeId: number): number => {
  return shiftTimeInHours(getSameDayShiftsForEmployee(employeeId));
};

const findJobRole = (shift: Shift): JobRole | undefined => {
  return getEntity(shift.jobRoleId, jobRoles.data);
};

const targetHoursStatus = (employee: Employee): string => {
  const shiftHours = Number(dynamicShiftHoursForWeek(employee.id).toFixed(2));
  const hoursPerWeek = Number(employee.workingHoursPerWeek.toFixed(2));
  return keyOfFirstTruthyValue({
    'text-gray-900': isZeroHourEmployee(employee) || shiftHours < hoursPerWeek,
    'text-green-800':
      !isZeroHourEmployee(employee) && shiftHours === hoursPerWeek,
    'text-red-600': !isZeroHourEmployee(employee) && shiftHours > hoursPerWeek,
  });
};

const getInfoMessage = (employee: Employee): string => {
  if (!selectedEmployees.value.includes(employee.id)) {
    return '';
  }
  if (!employeeWorksDate(employee)) {
    return i18n.tc('schedule.error.employeeDoesntWorkThisDayShort', 1, {
      employee: employee.firstName,
    });
  }
  if (hasUnpublishedShiftConflict(employee.id)) {
    return i18n.tc('warningMessage.conflictingUnpublishedShift', 1, {
      employee: fullName(employee),
    });
  }
  return '';
};

// The matching shifts update in realtime, so one can disappear after its spot has been filled.
watch(
  () => props.matchingShiftIds.length,
  (matchingShiftsTotal) => {
    selectedEmployees.value = selectedEmployees.value.slice(
      0,
      matchingShiftsTotal + 1,
    );
  },
);

watch(
  () => [
    ...respondedEmployees.value,
    ...availableEmployees.data.map((a) => a.employee),
  ],
  (employees) => {
    if (!employees.length) {
      employeeCosts.value = [];
      return;
    }
    employeeApi
      .bulkCalculateWorkingTimeCost({
        bulkCalculateWorkingTimeCostData: {
          bulkCalculateWorkingTimeCostData: employees.map((e) => ({
            employeeId: e.id,
            jobRoleId: adjustedShift.value.jobRoleId,
            startTime: adjustedShift.value.startsAt,
            endTime: adjustedShift.value.endsAt,
            unpaidWorkingPeriods: adjustedShift.value.scheduledBreaks
              .filter((b) => !b.paid)
              .map((b) => ({
                startTime: b.startsAt || adjustedShift.value.startsAt,
                endTime: addMinutes(
                  b.startsAt || adjustedShift.value.startsAt,
                  b.durationInMinutes,
                  timezone.value,
                ),
              })),
          })),
        },
      })
      .then(({ data }) => {
        employeeCosts.value = data.workingTimeCosts;
      });
  },
);

onMounted(async () => {
  await getAvailableEmployees();
  await fetchShiftData();
  sortEmployees.sortBy =
    shiftOpenAndUnassigned.value && hasResponses.value
      ? extendedSortOrders.value.ResponseTime
      : extendedSortOrders.value.AvailableHours;
});

return { __sfc: true,SortOrders, props, emit, open, userCanWithScope, availableEmployees, openShiftResponses, selectedEmployees, query, employees, jobRoles, employeeCosts, workPatterns, shiftCollection, weeklyOpenShiftResponses, schedules, groups, loading, initialising, showSort, sortEmployees, startOfWeek, timezone, currencyCode, openShiftsEnabled, shiftOpenAndUnassigned, hasResponses, extendedSortOrders, respondedEmployees, getShiftsForEmployee, filteredEmployees, assignedEmployee, adjustedShift, submitString, openShiftHistory, headerRight, shortTitle, longTitle, employeeIsAvailable, filteredAvailableEmployeesGrouped, filteredUnavailableEmployees, subHeading, getAvailableEmployees, fetchShiftData, employeeWorksDate, getShiftHoursForWeek, hasUnpublishedShiftConflict, employeeOverWeeklyHours, employeeCost, selectEmployee, autoSelect, changeEmployeeSortOrder, assignShift, createReplacement, employeeListItemDisabled, getSameWeekShiftsForEmployee, shiftTimeInHours, dynamicShiftHoursForWeek, getSameDayShiftsForEmployee, getShiftHoursForDay, findJobRole, targetHoursStatus, getInfoMessage, InputGroup, IconMessageBlock, Chip, LoadingDot, BottomSheet, SortDirections, getEntity, getEntityName, viewport, ShiftSummary, Button, Dialog, EmployeeListItem, InlineDialog, ToolTipWrapper, Search, dateTimeFormats, Icon, sortBy, formatCurrency, timeAgo, ShiftDialogHeader }
}

})